// Renders semantic canonical content blocks with Tailwind and shadcn primitives.

import React from 'react';
import { ASSETS } from '@airhead-games/shared/configs';
import type { Block } from '@airhead-games/shared/content';
import BlockFacts from '@/content/rendering/renderers/blocks/BlockFacts';
import RichTextRenderer from '@/content/rendering/renderers/RichTextRenderer';
import SortableTable from '@/content/rendering/renderers/blocks/SortableTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/utilities/cn';

interface BlockRendererProps {
  blocks?: Block[];
}

const blockTextClassName = 'text-base leading-7 text-foreground';

const BlockRenderer: React.FC<BlockRendererProps> = ({ blocks }) => {
  if (!blocks?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-5">
      {blocks.map((block, index) => {
        if (block.type === 'paragraph') {
          return (
            <p key={`${block.type}-${index}`} className={blockTextClassName}>
              <RichTextRenderer nodes={block.content} />
            </p>
          );
        }

        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag
              key={`${block.type}-${index}`}
              className={cn(
                'flex flex-col gap-2 pl-6 text-base leading-7',
                block.ordered ? 'list-decimal' : 'list-disc'
              )}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`${block.type}-${index}-${itemIndex}`}>
                  <RichTextRenderer nodes={item} />
                </li>
              ))}
            </ListTag>
          );
        }

        if (block.type === 'quote') {
          return (
            <blockquote
              key={`${block.type}-${index}`}
              className="border-l-4 border-primary pl-4 text-base leading-7 text-muted-foreground"
            >
              <RichTextRenderer nodes={block.content} />
              {block.attribution && (
                <footer className="mt-3 text-sm text-foreground">
                  <RichTextRenderer nodes={block.attribution} />
                </footer>
              )}
            </blockquote>
          );
        }

        if (block.type === 'image') {
          const maxWidth = block.maxWidth ?? 800;

          return (
            <figure
              key={`${block.type}-${index}`}
              className="mx-auto flex w-full flex-col gap-3"
              style={{
                maxWidth: `${maxWidth}px`,
              }}
            >
              <img
                src={ASSETS[block.imageAsset]}
                alt={block.imageAlt}
                loading="lazy"
                className="w-full rounded-xl border border-border bg-muted object-cover"
              />
              {block.caption && (
                <figcaption className="text-sm leading-6 text-muted-foreground">
                  <RichTextRenderer nodes={block.caption} />
                </figcaption>
              )}
            </figure>
          );
        }

        if (block.type === 'table') {
          return (
            <SortableTable
              key={`${block.type}-${index}`}
              columns={block.columns}
              rows={block.rows}
              variant={block.variant}
            />
          );
        }

        if (block.type === 'facts') {
          return (
            <BlockFacts key={`${block.type}-${index}`} facts={block.items} />
          );
        }

        if (block.type === 'callout') {
          return (
            <Alert
              key={`${block.type}-${index}`}
              className={cn(block.tone === 'muted' && 'bg-muted')}
            >
              <AlertDescription>
                <RichTextRenderer nodes={block.content} />
              </AlertDescription>
            </Alert>
          );
        }

        return null;
      })}
    </div>
  );
};

export default BlockRenderer;
